import { useCallback, useEffect, useState } from "react";
import {
  getMmsOnboardingStatus,
  MmsOnboardingStatusResponse,
} from "../../../../../../utils/mmsOnboardingApi";
import IGlobalConfig from "../../../../../../types/IGlobalConfig";
import { formatMmsApiErrorMessage } from "../../../../../../utils/mmsApiErrorMessage";

interface UseMmsOnboardingStatusProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  enabled?: boolean;
}

interface UseMmsOnboardingStatusReturn {
  status: MmsOnboardingStatusResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMmsOnboardingStatus({
  charityId,
  config,
  serviceAccessToken,
  enabled = true,
}: UseMmsOnboardingStatusProps): UseMmsOnboardingStatusReturn {
  const [status, setStatus] = useState<MmsOnboardingStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getMmsOnboardingStatus(
        config.adminServiceUrl,
        charityId,
        serviceAccessToken
      );
      setStatus(response);
    } catch (err) {
      const message = formatMmsApiErrorMessage(
        err,
        "Failed to load MMS onboarding status."
      );
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [charityId, config?.adminServiceUrl, enabled, serviceAccessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      refresh();
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [enabled, refresh]);

  return { status, loading, error, refresh };
}
